import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Code, Plus, Upload, Download, X, Link as LinkIcon, AlertCircle, Trash2, Lock, CheckCircle2 } from 'lucide-react';
import ProductDevPaywallModal from './ProductDevPaywallModal';
import AddonEntitlements from './AddonEntitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_LABEL = {
  submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved',
  in_progress: 'In Progress', qa: 'QA Testing', delivered: 'Delivered',
  on_hold: 'On Hold', rejected: 'Rejected',
};
const STATUS_BADGE = {
  submitted: 'bg-amber-100 text-amber-700', under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-violet-100 text-violet-700', in_progress: 'bg-indigo-100 text-indigo-700',
  qa: 'bg-cyan-100 text-cyan-700', delivered: 'bg-emerald-100 text-emerald-700',
  on_hold: 'bg-slate-100 text-slate-700', rejected: 'bg-rose-100 text-rose-700',
};
const PRODUCT_TYPES = [
  { value: 'saas', label: 'SaaS' },
  { value: 'web_app', label: 'Web Application' },
  { value: 'mobile_app', label: 'Mobile Application' },
  { value: 'internal_tool', label: 'Internal Tool' },
  { value: 'ai_product', label: 'AI Product' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'other', label: 'Other' },
];

const DRAFT_KEY = (firmId) => `product_dev_brief_draft_${firmId}`;

function ProjectBriefForm({ firmId, locked, onSubmitted, openPaywall, planConfig }) {
  const [form, setForm] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY(firmId)) || '{}') || {}; } catch { return {}; }
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    try { localStorage.setItem(DRAFT_KEY(firmId), JSON.stringify(next)); } catch (_e) { /* ignore */ }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const doSubmit = async () => {
    if (!form.title?.trim()) { setError('Project Name is required.'); return; }
    if (locked) {
      openPaywall(form);
      return;
    }
    setSubmitting(true); setError('');
    try {
      const refs = (form.reference_urls || '').split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: form.title.trim(),
        project_type: form.project_type || 'saas',
        description: form.description || '',
        reference_urls: refs,
        timeline: form.timeline || '',
        budget_range: form.budget_range || '',
        // Extended brief fields persisted in description as structured block
      };
      // Pack the structured brief into description so backend stays compatible
      const briefBlock = [
        form.description && `Description:\n${form.description}`,
        form.key_features && `Key Features Required:\n${form.key_features}`,
        form.target_users && `Target Users:\n${form.target_users}`,
        form.target_market && `Target Market:\n${form.target_market}`,
      ].filter(Boolean).join('\n\n');
      payload.description = briefBlock || form.description || '';

      const res = await axios.post(`${API}/web-dev/${firmId}/projects`, payload, { headers: authHeaders() });
      const projectId = res.data.id;
      // Upload pending files
      for (const file of pendingFiles) {
        const fd = new FormData(); fd.append('file', file);
        await axios.post(`${API}/web-dev/projects/${projectId}/attachments`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
      }
      try { localStorage.removeItem(DRAFT_KEY(firmId)); } catch (_e) { /* ignore */ }
      setForm({});
      setPendingFiles([]);
      onSubmitted();
      alert('Project brief submitted. Our team will reach out shortly.');
    } catch (e) {
      if (e.response?.status === 402) {
        openPaywall(form);
      } else {
        setError(e.response?.data?.detail || 'Failed to submit project brief.');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow-lg" data-testid="product-dev-brief-form">
      <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Project Brief</h3>
      <p className="text-sm text-slate-500 mb-5">Tell us about the product you want to build. Your draft is saved as you type.</p>

      {error && <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">{error}</div>}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Project Name *</label>
          <input type="text" value={form.title || ''} onChange={(e) => update('title', e.target.value)}
            placeholder="e.g., InsightCounsel - case-management SaaS"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="brief-title-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Product Type *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PRODUCT_TYPES.map((t) => (
              <button key={t.value} type="button" onClick={() => update('project_type', t.value)}
                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                  form.project_type === t.value
                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`brief-type-${t.value}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Project Description</label>
          <textarea value={form.description || ''} onChange={(e) => update('description', e.target.value)}
            placeholder="What problem does this solve? Who's it for? High-level vision."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[100px]"
            data-testid="brief-description-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Key Features Required</label>
          <textarea value={form.key_features || ''} onChange={(e) => update('key_features', e.target.value)}
            placeholder="Bullet the must-have features for launch."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[80px]"
            data-testid="brief-features-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Target Users</label>
            <input type="text" value={form.target_users || ''} onChange={(e) => update('target_users', e.target.value)}
              placeholder="e.g., Solo solicitors, Conveyancing teams"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="brief-users-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Target Market</label>
            <input type="text" value={form.target_market || ''} onChange={(e) => update('target_market', e.target.value)}
              placeholder="e.g., UK SMB law firms, US legal-tech"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="brief-market-input" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reference URLs</label>
          <textarea value={form.reference_urls || ''} onChange={(e) => update('reference_urls', e.target.value)}
            placeholder="One URL per line. Inspirations, competitors, existing tools."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 min-h-[60px]"
            data-testid="brief-refs-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Supporting Documents</label>
          <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50">
            <Upload className="w-4 h-4" />
            <span>Add files</span>
            <input type="file" className="hidden" multiple onChange={handleFileSelect} data-testid="brief-files-input" />
          </label>
          {pendingFiles.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {pendingFiles.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {f.name}
                  <button type="button" onClick={() => setPendingFiles((p) => p.filter((_, idx) => idx !== i))} className="ml-2 text-rose-500 hover:underline">remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button type="button" onClick={doSubmit} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50"
            data-testid="submit-brief-btn">
            {locked && <Lock className="w-4 h-4" />}
            {submitting ? 'Submitting…' : 'Submit Project Brief'}
          </button>
          {locked && (
            <span className="text-xs text-slate-500">You'll choose a plan and complete payment before the brief is submitted.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectList({ projects, canEdit, onChange }) {
  if (!projects.length) return null;
  const handleAttach = async (projectId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/web-dev/projects/${projectId}/attachments`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      alert('File uploaded.'); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed.'); }
  };
  const handleAdminUpdate = async (projectId, fields) => {
    try { await axios.patch(`${API}/web-dev/projects/${projectId}`, fields, { headers: authHeaders() }); onChange(); }
    catch (e) { alert(e.response?.data?.detail || 'Update failed.'); }
  };
  const handleDelete = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    await axios.delete(`${API}/web-dev/projects/${projectId}`, { headers: authHeaders() });
    onChange();
  };
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Your Projects</h3>
      {projects.map((p) => (
        <div key={p.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 soft-shadow" data-testid={`project-card-${p.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-900">{p.title}</h4>
              <p className="text-xs text-slate-500 capitalize">{(PRODUCT_TYPES.find((t) => t.value === p.project_type) || {}).label || p.project_type}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[p.status] || STATUS_BADGE.submitted}`}>{STATUS_LABEL[p.status] || p.status}</span>
          </div>
          {p.description && <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{p.description}</p>}
          <div className="w-full bg-slate-100 rounded-full h-2 mb-3">
            <div className="bg-gradient-to-r from-violet-500 to-indigo-600 h-2 rounded-full transition-all" style={{ width: `${p.progress_percent || 0}%` }} />
          </div>
          {p.latest_update && (
            <div className="text-xs bg-violet-50 border border-violet-100 text-violet-900 rounded p-2 mb-2"><span className="font-medium">Latest update:</span> {p.latest_update}</div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={(e) => handleAttach(p.id, e.target.files?.[0])} data-testid={`attach-${p.id}`} />
              <span className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-50"><Upload className="w-3 h-3" /> Add File</span>
            </label>
            {canEdit && (
              <>
                <select value={p.status} onChange={(e) => handleAdminUpdate(p.id, { status: e.target.value })} className="text-xs px-2 py-1.5 border border-slate-200 rounded-md bg-white" data-testid={`status-${p.id}`}>
                  {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input type="number" min={0} max={100} defaultValue={p.progress_percent || 0}
                  onBlur={(e) => handleAdminUpdate(p.id, { progress_percent: parseInt(e.target.value) || 0 })}
                  className="text-xs px-2 py-1.5 border border-slate-200 rounded-md bg-white w-20" data-testid={`progress-${p.id}`} />
                <button onClick={() => { const msg = window.prompt('Update message:'); if (msg) handleAdminUpdate(p.id, { latest_update: msg }); }} className="px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-md text-xs font-medium" data-testid={`add-update-${p.id}`}>Add Update</button>
                <button onClick={() => handleDelete(p.id)} className="ml-auto p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`delete-project-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WebDev({ user, selectedClient, planConfig }) {
  const firmId = selectedClient?.firmId || selectedClient?.firm_id || user?.firm_id || user?.firm?.id;
  const isAdmin = ['super_admin', 'internal_team'].includes(user?.role);
  const isClient = user?.role === 'client';

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [productDevState, setProductDevState] = useState('locked');
  const [customLimits, setCustomLimits] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const [projRes, pcRes] = await Promise.all([
        axios.get(`${API}/web-dev/${firmId}/projects`, { headers: authHeaders() }),
        axios.get(`${API}/firms/${firmId}/plan-config`, { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      setProjects(projRes.data || []);
      const pc = pcRes.data || planConfig;
      const ac = pc?.addons_config || {};
      const state = ac.product_dev || 'locked';
      setProductDevState(state);
      const cc = pc?.addons_custom?.product_dev;
      setCustomLimits(cc || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load projects.');
    } finally { setLoading(false); }
  }, [firmId, planConfig]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  // After payment redirect-back, refresh state
  useEffect(() => {
    const paying = localStorage.getItem('product_dev_paying_at');
    if (paying) {
      setTimeout(() => {
        localStorage.removeItem('product_dev_paying_at');
        fetchData();
      }, 1500);
    }
  }, [fetchData]);

  // Locked = not paid AND not custom AND not admin/team
  const locked = isClient && !(productDevState === 'enabled' || productDevState === 'custom');

  const openPaywall = () => setShowPaywall(true);
  const onOpenChat = () => {
    // Attempt to click the floating Messages bubble to open chat
    const bubble = document.querySelector('[data-testid="messages-bubble"]');
    if (bubble) bubble.click();
  };

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="webdev-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Product / Software Development</h1>
          <p className="text-slate-500 text-lg">Submit a project brief - we'll plan, design and build the product.</p>
        </div>
        {locked && (
          <button onClick={openPaywall} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30" data-testid="open-paywall-btn">
            <Lock className="w-4 h-4" /> View Plans
          </button>
        )}
      </div>

      <AddonEntitlements firmId={firmId} addonKey="product_dev" />

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{error}</div>}

      {!firmId ? (
        <div className="bg-white/70 rounded-2xl border border-white/50 p-12 text-center text-slate-500 flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" /> Select a client to view projects.</div>
      ) : (
        <>
          {productDevState === 'custom' && customLimits && (
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-2xl p-5" data-testid="product-dev-custom-banner">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-slate-900">Custom Plan Assigned</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {customLimits.project_tier && <div><span className="text-slate-500">Project Tier:</span> <span className="font-medium text-slate-900">{customLimits.project_tier}</span></div>}
                {customLimits.development_hours && <div><span className="text-slate-500">Development Hours:</span> <span className="font-medium text-slate-900">{customLimits.development_hours}</span></div>}
                {customLimits.special_scope && <div className="md:col-span-3"><span className="text-slate-500">Scope:</span> <span className="font-medium text-slate-900">{customLimits.special_scope}</span></div>}
              </div>
            </div>
          )}

          {(isClient || isAdmin) && (
            <ProjectBriefForm
              firmId={firmId}
              locked={locked}
              openPaywall={openPaywall}
              onSubmitted={() => setRefreshKey((k) => k + 1)}
              planConfig={planConfig}
            />
          )}

          {loading ? (
            <div className="bg-white/70 rounded-2xl border border-white/50 p-10 text-center text-slate-500">Loading…</div>
          ) : projects.length ? (
            <ProjectList projects={projects} canEdit={isAdmin} onChange={() => setRefreshKey((k) => k + 1)} />
          ) : null}
        </>
      )}

      <ProductDevPaywallModal
        open={showPaywall}
        firmId={firmId}
        onClose={() => setShowPaywall(false)}
        onSavePendingBrief={() => { /* draft already in localStorage via form */ }}
        onOpenChat={onOpenChat}
      />
    </div>
  );
}
